import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  ArrowLeft, Loader2, Crown, Copy, Check, ShieldCheck, Star, UserMinus, Trash2, LogOut, Users,
} from "lucide-react";

export default function PodDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pod, setPod] = useState(null);
  const [copied, setCopied] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/pods/${id}`);
      setPod(data);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Pod not found");
      navigate("/pods");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const copy = () => {
    navigator.clipboard.writeText(pod.invite_code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const removeMember = async (m) => {
    try {
      await api.delete(`/pods/${id}/members/${m.id}`);
      toast.success(`Removed ${m.display_name}`);
      setRemoveTarget(null);
      await load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const leavePod = async () => {
    try {
      await api.post(`/pods/${id}/leave`);
      toast.success("You left the pod");
      navigate("/pods");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const deletePod = async () => {
    try {
      await api.delete(`/pods/${id}`);
      toast.success("Pod deleted");
      navigate("/pods");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  if (!pod) return <div className="flex justify-center py-20 text-[#6B7280]"><Loader2 className="animate-spin" /></div>;
  const isOwner = pod.is_owner;

  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      <button onClick={() => navigate("/pods")} data-testid="pod-detail-back" className="flex items-center gap-1.5 text-sm font-semibold text-[#6B7280] mb-4 hover:text-[#1A1D20]">
        <ArrowLeft size={17} /> Back to pods
      </button>

      <div className="bg-white rounded-3xl border border-gray-100 shadow-soft p-6" data-testid="pod-detail">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="font-heading font-black text-2xl text-[#1A1D20] flex items-center gap-2">
              {pod.name} {isOwner && <Crown size={18} className="text-[#FFB020]" />}
            </h1>
            <p className="text-sm text-[#6B7280] mt-1 flex items-center gap-1.5"><Users size={14} /> {pod.member_count} member{pod.member_count === 1 ? "" : "s"}</p>
          </div>
          <button data-testid="pod-detail-copy" onClick={copy}
            className="flex items-center gap-1.5 bg-muted rounded-full px-3 py-2 text-sm font-bold text-[#4A5361] hover:bg-gray-200 shrink-0">
            {copied ? <Check size={14} className="text-[#2F8F6E]" /> : <Copy size={14} />} {pod.invite_code}
          </button>
        </div>
      </div>

      <h2 className="font-heading font-extrabold text-lg text-[#1A1D20] mt-6 mb-3">Members</h2>
      <div className="space-y-2.5">
        {pod.members.map((m) => (
          <div key={m.id} data-testid={`member-${m.id}`} className="bg-white rounded-2xl border border-gray-100 shadow-soft p-4 flex items-center gap-3">
            <button onClick={() => navigate(`/profile/${m.id}`)} className="w-11 h-11 rounded-2xl bg-primary/15 text-primary font-black text-lg flex items-center justify-center shrink-0">
              {m.display_name?.[0]?.toUpperCase() || "?"}
            </button>
            <button onClick={() => navigate(`/profile/${m.id}`)} className="min-w-0 flex-1 text-left">
              <p className="font-bold text-[#1A1D20] flex items-center gap-1.5 truncate">
                {m.display_name}
                {m.is_owner && <Crown size={13} className="text-[#FFB020]" />}
                {m.id === user?.id && <span className="text-[10px] font-bold text-[#6B7280]">(You)</span>}
              </p>
              <div className="flex items-center gap-2 text-xs text-[#6B7280] mt-0.5">
                <span className="flex items-center gap-1"><Star size={11} className="text-[#FFB020] fill-[#FFB020]" /> {(m.rating_avg || 0).toFixed(1)}</span>
                <span>·</span>
                <span className="flex items-center gap-1"><ShieldCheck size={11} className={m.verified ? "text-[#2F8F6E]" : "text-[#9CA3AF]"} /> Trust {m.trust_score}</span>
                <span>·</span>
                <span>{m.errands_completed} done</span>
              </div>
            </button>
            {isOwner && !m.is_owner && (
              <button data-testid={`remove-member-${m.id}`} onClick={() => setRemoveTarget(m)} aria-label="Remove member"
                className="w-9 h-9 rounded-full flex items-center justify-center text-[#B23B3B] bg-[#FBE9E9]/60 hover:bg-[#FBE9E9] shrink-0">
                <UserMinus size={16} />
              </button>
            )}
          </div>
        ))}
      </div>

      <div className="mt-6">
        {isOwner ? (
          <Button data-testid="pod-detail-delete" onClick={() => setDeleteOpen(true)} variant="outline"
            className="w-full rounded-full h-12 font-bold border-2 border-[#F1D5D5] text-[#B23B3B] hover:bg-[#FBE9E9]">
            <Trash2 size={16} className="mr-2" /> Delete this pod
          </Button>
        ) : (
          <Button data-testid="pod-detail-leave" onClick={leavePod} variant="outline"
            className="w-full rounded-full h-12 font-bold border-2">
            <LogOut size={16} className="mr-2" /> Leave this pod
          </Button>
        )}
      </div>

      <AlertDialog open={!!removeTarget} onOpenChange={(o) => !o && setRemoveTarget(null)}>
        <AlertDialogContent className="rounded-3xl" data-testid="remove-member-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Remove {removeTarget?.display_name}?</AlertDialogTitle>
            <AlertDialogDescription>They'll lose access to this pod's errands. They can rejoin later with the invite code.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full font-semibold">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="remove-member-confirm" onClick={(ev) => { ev.preventDefault(); removeMember(removeTarget); }}
              className="rounded-full font-bold bg-[#B23B3B] hover:bg-[#9c3333]">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent className="rounded-3xl" data-testid="pod-detail-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete "{pod.name}"?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes the pod for all members. Errands posted to it become public. This can't be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full font-semibold">Keep pod</AlertDialogCancel>
            <AlertDialogAction data-testid="pod-detail-delete-confirm" onClick={(ev) => { ev.preventDefault(); deletePod(); }}
              className="rounded-full font-bold bg-[#B23B3B] hover:bg-[#9c3333]">Delete pod</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
